/**
 * navigation/linking.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The platform auth hand-off, on native.
 *
 * This app owns no login screen. On Android/iOS the user signs in through
 * skplatform-fe, which returns them here on the registered scheme:
 *
 *   skcompanycash://auth?access_token=…&refresh_token=…
 *
 * The token is stored (storage/tokenStore) and the session re-probed. On web
 * none of this applies — the platform's httpOnly cookie is already in the
 * browser's jar, and there is nothing for JavaScript to read or carry.
 *
 * The token is deliberately NOT logged, anywhere, at any level.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Linking } from 'react-native';
import { acceptHandoff } from '../services/session/session.service';
import { isWeb } from '../lib/responsive';

/** The scheme registered in AndroidManifest.xml and Info.plist. */
export const APP_SCHEME = 'skcompanycash';

export interface HandoffTokens {
  accessToken: string;
  refreshToken?: string;
}

/**
 * Pull the tokens out of a hand-off URL, or null if this is not one.
 *
 * Parsed by hand rather than with `URL`: React Native's URL polyfill does not
 * reliably expose `searchParams` for a custom scheme, and getting a query
 * string wrong here means a silent failure to sign in.
 */
export function parseHandoff(url: string | null | undefined): HandoffTokens | null {
  if (!url || !url.startsWith(`${APP_SCHEME}://`)) return null;

  const queryStart = url.indexOf('?');
  if (queryStart === -1) return null;

  const params = new Map<string, string>();
  for (const pair of url.slice(queryStart + 1).split('&')) {
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    params.set(
      decodeURIComponent(pair.slice(0, eq)),
      decodeURIComponent(pair.slice(eq + 1)),
    );
  }

  const accessToken = params.get('access_token');
  if (!accessToken) return null;

  const refreshToken = params.get('refresh_token');
  return refreshToken ? { accessToken, refreshToken } : { accessToken };
}

/**
 * Start listening for the hand-off. Returns a teardown function.
 *
 * `onSession` is called after the token is stored so the caller can re-probe —
 * SessionContext passes its own `refresh`.
 *
 * Both entry points are handled: `getInitialURL` for a cold start (the app was
 * not running when the link was opened) and the `url` event for a warm one.
 * Handling only the event is the classic bug — a cold start silently drops the
 * token and the user lands on the sign-in prompt they just came from.
 */
export function startAuthHandoffListener(onSession: () => void): () => void {
  if (isWeb) return () => undefined;

  let cancelled = false;

  const consume = async (url: string | null): Promise<void> => {
    const tokens = parseHandoff(url);
    if (!tokens || cancelled) return;
    await acceptHandoff(tokens.accessToken, tokens.refreshToken);
    if (!cancelled) onSession();
  };

  void Linking.getInitialURL().then((url) => consume(url));
  const subscription = Linking.addEventListener('url', ({ url }) => void consume(url));

  return () => {
    cancelled = true;
    subscription.remove();
  };
}
